import { createFileRoute, Link, useParams, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  MessageSquare,
  Activity,
  FileText,
  RefreshCw,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { useState } from 'react';
import { api, ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { StatusPill } from '@/components/ui/status-pill';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { formatAud, taxYearLabel, cn } from '@/lib/utils';
import { humanizeAuditAction } from '@/lib/audit';
import {
  ConversationMessages,
  useConversationMessages,
} from '@/components/admin/ConversationThread';
import type {
  AdminModuleAuditListResponse,
  AdminModuleAuditEntry,
} from '@/api/generated/types.gen';

// The admin detail page draws everything from the Administrator-only
// GET /admin/returns/{id} endpoint (AdminGetReturnDetail). The taxpayer-scoped
// /returns/{id} + section endpoints all filter by the current user, so they 404
// when an admin opens someone else's return — which is why this page never used
// to open. One admin call returns the whole structured return.
const TABS = ['Overview', 'Sync', 'Transcript', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const TAB_ICONS: Record<Tab, typeof FileText> = {
  Overview: FileText,
  Sync: RefreshCw,
  Transcript: MessageSquare,
  Audit: Activity,
};

const TAB_ITEMS: TabItem<Tab>[] = TABS.map((t) => ({ value: t, label: t, icon: TAB_ICONS[t] }));

const isTab = (v: unknown): v is Tab => typeof v === 'string' && (TABS as readonly string[]).includes(v);

export const Route = createFileRoute('/admin/returns/$id')({
  // Keep the active tab in the URL (?tab=Sync) so a page refresh — or a shared/bookmarked
  // link — reopens the same view instead of snapping back to Overview. tab is OPTIONAL
  // (absent = Overview) so the existing <Link to="/admin/returns/$id"> call sites elsewhere
  // in the admin UI don't have to start passing a search param.
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => ({
    tab: isTab(search.tab) ? search.tab : undefined,
  }),
  component: AdminReturnDetail,
});

function AdminReturnDetail() {
  const { id } = useParams({ from: '/admin/returns/$id' });
  // Tab is URL-backed (see Route.validateSearch) so refresh/deep-links land on the same
  // view. Absent search param = the default Overview tab.
  const tab = Route.useSearch().tab ?? 'Overview';
  const navigate = useNavigate({ from: '/admin/returns/$id' });
  // Drop the param when returning to Overview so the default stays a clean URL.
  const setTab = (next: Tab) =>
    navigate({
      search: (prev) => ({ ...prev, tab: next === 'Overview' ? undefined : next }),
      replace: true,
    });

  const detail = useQuery({
    queryKey: ['admin', 'returns', id, 'detail'],
    queryFn: () => api<ReturnDetail>(`/admin/returns/${id}`),
    retry: false,
  });

  const pi = detail.data?.personalInformation;
  const name = `${pi?.firstName ?? ''} ${pi?.familyName ?? ''}`.trim();

  return (
    <div className="space-y-6">
      <Link
        to="/admin/returns"
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-(--color-ink-muted) hover:text-(--color-navy)"
      >
        <ChevronLeft className="size-4" /> All returns
      </Link>

      <header className="flex items-start justify-between gap-6">
        <div>
          <div className="section-ref">Return</div>
          <h1 className="mt-1.5 font-serif text-3xl font-medium tracking-tight">
            {detail.isLoading
              ? 'Loading…'
              : detail.isError
                ? 'Return unavailable'
                : name || 'Unnamed return'}
          </h1>
          <div className="mt-1 gov-rule" aria-hidden />
          <p className="mt-3 text-[13px] text-(--color-ink-muted)">
            FY {detail.data ? taxYearLabel(detail.data.year) : '—'} · Return #{id}
          </p>
        </div>
      </header>

      {detail.isError && (
        <Card>
          <CardContent className="flex items-start gap-2 p-7 text-[13px] text-(--color-maroon)">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span>Could not load this return: {extractError(detail.error)}</span>
          </CardContent>
        </Card>
      )}

      {detail.data && <AdminActions returnId={id} currentStatus={detail.data.status} />}

      <Tabs items={TAB_ITEMS} value={tab} onValueChange={setTab} ariaLabel="Return detail views" />

      {tab === 'Overview' && <OverviewTab detail={detail.data} loading={detail.isLoading} />}
      {tab === 'Sync' && <SyncTab returnId={id} />}
      {tab === 'Transcript' && <TranscriptTab returnId={id} />}
      {tab === 'Audit' && <AuditTab returnId={id} />}
    </div>
  );
}

const STATUS_OPTIONS = ['Draft', 'Lodged', 'LodgeFailed'] as const;

// detail.status comes from the TaxReturnStatus enum. The admin detail endpoint
// serializes it as the integer value (Draft=0, Lodged=1, LodgeFailed=2), whereas
// the list endpoint emits the string name — so accept both and normalise to the
// PascalCase option the dropdown and the POST /status endpoint expect. Guarding
// only with `?? ''` let a number through and crashed on `.toLowerCase()`.
function mapStatus(s?: string | number): string {
  switch (typeof s === 'number' ? s : (s ?? '').toLowerCase()) {
    case 1:
    case 'lodged':
      return 'Lodged';
    case 2:
    case 'lodgefailed':
      return 'LodgeFailed';
    default:
      return 'Draft';
  }
}

function AdminActions({ returnId, currentStatus }: { returnId: string; currentStatus?: string | number }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>(() => mapStatus(currentStatus));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveStatus = useMutation({
    mutationFn: () => api(`/admin/returns/${returnId}/status`, { method: 'POST', json: { status } }),
    onError: (e: unknown) => setError(extractError(e)),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['admin', 'returns'] });
    },
  });

  const del = useMutation({
    mutationFn: () => api(`/admin/returns/${returnId}`, { method: 'DELETE' }),
    onError: (e: unknown) => setError(extractError(e)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'returns'] });
      navigate({ to: '/admin/returns' });
    },
  });

  return (
    <Card>
      <CardContent className="space-y-4 p-7">
        <div>
          <div className="section-ref">Admin actions</div>
          <h2 className="mt-1 font-serif text-xl font-medium">Manage this return</h2>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-(--radius) border border-(--color-maroon) bg-(--color-maroon-soft) px-3 py-2 text-[13px] text-(--color-maroon)">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <label className="block text-[12px] font-semibold uppercase tracking-[0.14em] text-(--color-ink-muted)">
              Status
            </label>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-44">
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
          <Button onClick={() => saveStatus.mutate()} disabled={saveStatus.isPending}>
            {saveStatus.isPending ? 'Saving…' : 'Save status'}
          </Button>

          <div className="flex-1" />

          {confirmDelete ? (
            <div className="flex items-center gap-2 rounded-(--radius) border border-(--color-rule) bg-(--color-paper-2) p-2">
              <span className="text-[13px] text-(--color-ink)">Delete this return permanently?</span>
              <Button variant="danger" size="sm" disabled={del.isPending} onClick={() => del.mutate()}>
                {del.isPending ? 'Deleting…' : 'Confirm delete'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button variant="secondary" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="size-4 text-(--color-maroon)" />
              Delete return
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function extractError(e: unknown): string {
  if (e instanceof ApiError) {
    const p = e.problem as { error?: string; errors?: string[]; title?: string } | null;
    if (p?.error) return p.error;
    if (p?.errors?.length) return p.errors.join(' · ');
    if (p?.title) return p.title;
    return `Request failed (HTTP ${e.status})`;
  }
  return 'Request failed';
}

// ---------------------------------------------------------------------------
// Overview — rendered from the structured admin detail response. Income and
// deductions arrive as the full nested sections (not the taxpayer-scoped
// flattened lists), so we collapse them into simple display lines here.
// ---------------------------------------------------------------------------

interface ReturnDetail {
  id: number;
  year: number;
  status?: string | number;
  personalInformation?: {
    taxFileNumber?: string;
    dateOfBirth?: string;
    firstName?: string;
    familyName?: string;
    mobilePhone?: string;
    areaCode?: string;
    phoneNumber?: string;
    isFinalReturn?: boolean;
  };
  occupationDetail?: { occupation?: string };
  salaryOrWage?: { items?: { payersAbn?: string; income?: number; taxWithheld?: number }[] };
  dividends?: { totalUnfrankedAmount?: number; totalFrankedAmount?: number }[];
  grossInterests?: { totalAmount?: number }[];
  auGovernmentAllowances?: { amount?: number }[];
  auGovernmentPensions?: { amount?: number }[];
  workRelatedCarExpense?: object;
  workRelatedComputerExpense?: { items?: { amountClaimed?: number }[] };
  workRelatedBooksAndOtherExpense?: { items?: { amountClaimed?: number }[] };
  workRelatedTravelExpense?: { items?: { amount?: number }[] };
  workRelatedUniformExpense?: { items?: { amount?: number }[] };
  workRelatedToolsExpense?: { amountLess?: number; amountMore?: number };
  workRelatedMobilePhoneExpense?: { amount?: number };
  workRelatedUnionFeeExpense?: { amount?: number };
  workRelatedSunProtectionExpense?: { amount?: number };
  workRelatedInternetAccessExpense?: { amount?: number };
  workRelatedParkingTollExpense?: { parkingAmount?: number; tollAmount?: number };
  workRelatedHomeOfficeExpense?: {
    items?: { amount?: number }[];
    numberOfWorkingWeeks?: number;
    averageWorkingHoursPerWeek?: number;
  };
  workRelatedDedicatedOfficeExpense?: { officeArea?: number; homeArea?: number; reimbursed?: boolean };
  workRelatedSelfEducationOtherExpense?: { items?: { amount?: number }[] };
  managingTaxAffairCost?: {
    costOfManagingTaxAffairAmount?: number;
    interestChargedByAtoAmount?: number;
    legalOrLawyerCostsAmount?: number;
  };
  lowValuePoolDeduction?: { investmentAmount?: number; rentalAmount?: number; otherAmount?: number };
  interestDeduction?: { amount?: number };
  dividendDeduction?: { amount?: number };
  undeductedPurchasePriceDeduction?: { amount?: number };
  projectPoolDeduction?: { amount?: number };
  forestryManagedInvestmentSchemeDeduction?: { amount?: number };
  otherDeduction?: { insuranceAmount?: number; foreignExchangeAmount?: number; otherDeductionsAmount?: number };
  giftOrDonation?: { items?: { donationAmount?: number }[] };
  personalSuperannuationContribution?: { items?: { amount?: number }[] };
  rentalProperties?: {
    address?: string;
    grossRent?: number;
    otherRentalIncome?: number;
    interestOnLoans?: number;
    capitalWorksDeduction?: number;
    propertyAgentFees?: number;
    councilRates?: number;
    waterRates?: number;
    insurance?: number;
    repairsAndMaintenance?: number;
    landTax?: number;
    otherDeductions?: number;
  }[];
  cryptoHolding?: { hasCrypto?: boolean; netProfitOrLoss?: number; heldOverTwelveMonths?: boolean };
  soleTraderBusiness?: {
    hasSoleTraderIncome?: boolean;
    isRegisteredForGst?: boolean;
    businessNames?: string[];
    grossSalesTurnover?: number;
    otherBusinessIncome?: number;
    purchases?: number;
    contractorPayments?: number;
    employeeWages?: number;
    employeeSuperannuation?: number;
    rentOrLease?: number;
    motorVehicleExpenses?: number;
    repairsAndMaintenance?: number;
    depreciation?: number;
    insurance?: number;
    interestExpenses?: number;
    utilities?: number;
    phoneAndInternet?: number;
    advertising?: number;
    officeSupplies?: number;
    accountingFees?: number;
    bankFees?: number;
    travel?: number;
    otherExpenses?: number;
  };
  boatInsurance?: { provider?: string; annualPremium?: number };
  carInsurance?: { provider?: string; annualPremium?: number };
  homeAndContentsInsurance?: { provider?: string; annualPremium?: number };
  petInsurance?: { provider?: string; annualPremium?: number };
  mortgage?: { provider?: string; amount?: number; interestRate?: number };
  dependentChildren?: { number?: number };
  medicareLevySurcharge?: { hasFullYearPrivateHospitalCover?: boolean };
  spouseInformation?: { spouseFirstName?: string; spouseFamilyName?: string; spouseTaxableIncome?: number };
  personalResidencyDetail?: {
    isAustralianCitizen?: boolean;
    visaType?: number;
    otherVisaType?: string;
    arrivalInAustralia?: string;
    departureFromAustralia?: string;
    isFullYearResidentCitizen?: boolean;
  };
  personalBankDetail?: { bsbNumber?: string; accountNumber?: string; accountName?: string };
  personalHomeAddress?: { addressLine?: string; suburb?: string; state?: string; postcode?: string };
  personalPostalAddress?: { addressLine?: string; suburb?: string; state?: string; postcode?: string };
  personalEducationAndOtherDebt?: {
    haveHelpDept?: boolean;
    remainingHelpDebt?: number;
    haveSfssDebt?: boolean;
    remainingSfssDebt?: number;
    haveOtherTaxRelatedDebts?: boolean;
  };
  incomeTest?: {
    iT1TotalReportableFringeBenefits?: number;
    iT2ReportableSuperContributions?: number;
    iT3TaxFreeGovernmentPensions?: number;
    iT4TargetForeignIncome?: number;
    iT5NetFinancialInvestmentLoss?: number;
    iT6NetRentalPropertyLoss?: number;
    iT7ChildSupportPaid?: number;
  };
}

interface DisplayLine {
  label: string;
  amount: number | null; // null → section present but no single dollar figure
  sub?: string;
}

function sumBy<T>(items: T[] | undefined, pick: (x: T) => number | undefined): number {
  return (items ?? []).reduce((total, x) => total + (pick(x) ?? 0), 0);
}

function incomeLines(d: ReturnDetail): DisplayLine[] {
  const lines: DisplayLine[] = [];
  for (const it of d.salaryOrWage?.items ?? []) {
    lines.push({
      label: it.payersAbn ? `Salary or wage · ABN ${it.payersAbn}` : 'Salary or wage',
      amount: it.income ?? 0,
      sub: it.taxWithheld ? `${formatAud(it.taxWithheld)} tax withheld` : undefined,
    });
  }
  for (const dv of d.dividends ?? []) {
    lines.push({
      label: 'Dividends',
      amount: (dv.totalUnfrankedAmount ?? 0) + (dv.totalFrankedAmount ?? 0),
    });
  }
  for (const gi of d.grossInterests ?? []) {
    lines.push({ label: 'Gross interest', amount: gi.totalAmount ?? 0 });
  }
  for (const a of d.auGovernmentAllowances ?? []) {
    lines.push({ label: 'Government allowance', amount: a.amount ?? 0 });
  }
  for (const p of d.auGovernmentPensions ?? []) {
    lines.push({ label: 'Government pension', amount: p.amount ?? 0 });
  }
  return lines;
}

function deductionLines(d: ReturnDetail): DisplayLine[] {
  const out: DisplayLine[] = [];
  const add = (section: unknown, label: string, amount: number | null) => {
    if (section == null) return;
    if (amount !== null && amount <= 0) return; // present but empty → skip the noise
    out.push({ label, amount });
  };

  add(d.workRelatedCarExpense, 'Work-related car', null);
  add(d.workRelatedComputerExpense, 'Computer & equipment', sumBy(d.workRelatedComputerExpense?.items, (i) => i.amountClaimed));
  add(d.workRelatedBooksAndOtherExpense, 'Books & periodicals', sumBy(d.workRelatedBooksAndOtherExpense?.items, (i) => i.amountClaimed));
  add(d.workRelatedTravelExpense, 'Travel', sumBy(d.workRelatedTravelExpense?.items, (i) => i.amount));
  add(d.workRelatedUniformExpense, 'Uniform & clothing', sumBy(d.workRelatedUniformExpense?.items, (i) => i.amount));
  add(d.workRelatedToolsExpense, 'Tools & equipment', (d.workRelatedToolsExpense?.amountLess ?? 0) + (d.workRelatedToolsExpense?.amountMore ?? 0));
  add(d.workRelatedMobilePhoneExpense, 'Mobile phone', d.workRelatedMobilePhoneExpense?.amount ?? 0);
  add(d.workRelatedUnionFeeExpense, 'Union & professional fees', d.workRelatedUnionFeeExpense?.amount ?? 0);
  add(d.workRelatedSunProtectionExpense, 'Sun protection', d.workRelatedSunProtectionExpense?.amount ?? 0);
  add(d.workRelatedInternetAccessExpense, 'Internet access', d.workRelatedInternetAccessExpense?.amount ?? 0);
  add(d.workRelatedParkingTollExpense, 'Parking & tolls', (d.workRelatedParkingTollExpense?.parkingAmount ?? 0) + (d.workRelatedParkingTollExpense?.tollAmount ?? 0));
  // WFH can be the actual-cost method (item amounts) OR the fixed-rate method
  // (weeks × hours, no items). Surface whichever the taxpayer used — otherwise a
  // fixed-rate claim sums to $0 and is silently dropped.
  const wfh = d.workRelatedHomeOfficeExpense;
  if (wfh) {
    const wfhItems = sumBy(wfh.items, (i) => i.amount);
    if (wfhItems > 0) {
      out.push({ label: 'Home office (actual cost)', amount: wfhItems });
    } else if ((wfh.numberOfWorkingWeeks ?? 0) > 0) {
      out.push({
        label: 'Home office (fixed rate)',
        amount: null,
        sub: `${wfh.numberOfWorkingWeeks}w × ${wfh.averageWorkingHoursPerWeek ?? 0}h/wk`,
      });
    }
  }
  // Dedicated-office (occupancy method): no single dollar figure on the return — the
  // amount comes from a full home running-cost schedule — so surface it as provided.
  const office = d.workRelatedDedicatedOfficeExpense;
  if ((office?.officeArea ?? 0) > 0) {
    out.push({
      label: 'Home office (occupancy method)',
      amount: null,
      sub: `${office!.officeArea}/${office!.homeArea ?? 0} m²`,
    });
  }
  add(d.workRelatedSelfEducationOtherExpense, 'Self-education', sumBy(d.workRelatedSelfEducationOtherExpense?.items, (i) => i.amount));
  add(
    d.managingTaxAffairCost,
    'Managing tax affairs',
    (d.managingTaxAffairCost?.costOfManagingTaxAffairAmount ?? 0) +
      (d.managingTaxAffairCost?.interestChargedByAtoAmount ?? 0) +
      (d.managingTaxAffairCost?.legalOrLawyerCostsAmount ?? 0),
  );
  add(
    d.lowValuePoolDeduction,
    'Low-value pool',
    (d.lowValuePoolDeduction?.investmentAmount ?? 0) + (d.lowValuePoolDeduction?.rentalAmount ?? 0) + (d.lowValuePoolDeduction?.otherAmount ?? 0),
  );
  add(d.interestDeduction, 'Interest deductions', d.interestDeduction?.amount ?? 0);
  add(d.dividendDeduction, 'Dividend deductions', d.dividendDeduction?.amount ?? 0);
  add(d.undeductedPurchasePriceDeduction, 'Undeducted purchase price', d.undeductedPurchasePriceDeduction?.amount ?? 0);
  add(d.projectPoolDeduction, 'Project pool', d.projectPoolDeduction?.amount ?? 0);
  add(d.forestryManagedInvestmentSchemeDeduction, 'Forestry managed investment', d.forestryManagedInvestmentSchemeDeduction?.amount ?? 0);
  add(
    d.otherDeduction,
    'Other deductions',
    (d.otherDeduction?.insuranceAmount ?? 0) + (d.otherDeduction?.foreignExchangeAmount ?? 0) + (d.otherDeduction?.otherDeductionsAmount ?? 0),
  );
  add(d.giftOrDonation, 'Gifts & donations', sumBy(d.giftOrDonation?.items, (i) => i.donationAmount));
  add(d.personalSuperannuationContribution, 'Personal super contributions', sumBy(d.personalSuperannuationContribution?.items, (i) => i.amount));
  return out;
}

function rentalLines(d: ReturnDetail): DisplayLine[] {
  return (d.rentalProperties ?? []).map((r, i) => {
    const income = (r.grossRent ?? 0) + (r.otherRentalIncome ?? 0);
    const deductions =
      (r.interestOnLoans ?? 0) +
      (r.capitalWorksDeduction ?? 0) +
      (r.propertyAgentFees ?? 0) +
      (r.councilRates ?? 0) +
      (r.waterRates ?? 0) +
      (r.insurance ?? 0) +
      (r.repairsAndMaintenance ?? 0) +
      (r.landTax ?? 0) +
      (r.otherDeductions ?? 0);
    return {
      label: r.address?.trim() || `Property ${i + 1}`,
      amount: income,
      sub: `${formatAud(deductions)} deductions · net ${formatAud(income - deductions)}`,
    };
  });
}

// Sections that are captured on the return but don't fit the income/deduction
// line lists — surfaced as plain label/value rows so admins see the whole return.
function otherDetailLines(d: ReturnDetail): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];

  if ((d.dependentChildren?.number ?? 0) > 0) {
    rows.push({ label: 'Dependent children', value: String(d.dependentChildren!.number) });
  }
  if (d.spouseInformation?.spouseFirstName || d.spouseInformation?.spouseFamilyName) {
    const name = `${d.spouseInformation.spouseFirstName ?? ''} ${d.spouseInformation.spouseFamilyName ?? ''}`.trim();
    rows.push({ label: 'Spouse', value: name || 'Provided' });
    if ((d.spouseInformation.spouseTaxableIncome ?? 0) > 0) {
      rows.push({ label: 'Spouse taxable income', value: formatAud(d.spouseInformation.spouseTaxableIncome!) });
    }
  }
  if (d.medicareLevySurcharge) {
    rows.push({
      label: 'Private hospital cover (full year)',
      value: d.medicareLevySurcharge.hasFullYearPrivateHospitalCover ? 'Yes' : 'No',
    });
  }

  const insurances: [string, ReturnDetail['boatInsurance']][] = [
    ['Boat insurance', d.boatInsurance],
    ['Car insurance', d.carInsurance],
    ['Home & contents insurance', d.homeAndContentsInsurance],
    ['Pet insurance', d.petInsurance],
  ];
  for (const [label, ins] of insurances) {
    if (ins && (ins.provider || (ins.annualPremium ?? 0) > 0)) {
      rows.push({
        label,
        value: `${ins.provider || '—'}${ins.annualPremium ? ` · ${formatAud(ins.annualPremium)}/yr` : ''}`,
      });
    }
  }
  if (d.mortgage && (d.mortgage.provider || (d.mortgage.amount ?? 0) > 0)) {
    rows.push({
      label: 'Mortgage',
      value: `${d.mortgage.provider || '—'}${d.mortgage.amount ? ` · ${formatAud(d.mortgage.amount)}` : ''}`,
    });
  }
  return rows;
}

// Personal sub-sections (residency, bank, addresses, study debts) as label/value rows.
function personalDetailLines(d: ReturnDetail): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];

  const res = d.personalResidencyDetail;
  if (res) {
    rows.push({ label: 'Australian citizen', value: res.isAustralianCitizen ? 'Yes' : 'No' });
    if (!res.isAustralianCitizen) {
      if (res.otherVisaType || res.visaType != null) {
        rows.push({ label: 'Visa type', value: res.otherVisaType || String(res.visaType ?? '—') });
      }
      if (res.arrivalInAustralia) rows.push({ label: 'Arrived in Australia', value: res.arrivalInAustralia.slice(0, 10) });
      if (res.departureFromAustralia) rows.push({ label: 'Departed Australia', value: res.departureFromAustralia.slice(0, 10) });
    }
    if (res.isFullYearResidentCitizen != null) {
      rows.push({ label: 'Full-year resident', value: res.isFullYearResidentCitizen ? 'Yes' : 'No' });
    }
  }

  const bank = d.personalBankDetail;
  if (bank && (bank.bsbNumber || bank.accountNumber || bank.accountName)) {
    rows.push({
      label: 'Bank account',
      value: [bank.accountName, bank.bsbNumber && `BSB ${bank.bsbNumber}`, bank.accountNumber].filter(Boolean).join(' · ') || '—',
    });
  }

  const addr = (label: string, a?: ReturnDetail['personalHomeAddress']) => {
    if (a && (a.addressLine || a.suburb || a.state || a.postcode)) {
      rows.push({ label, value: [a.addressLine, a.suburb, a.state, a.postcode].filter(Boolean).join(', ') });
    }
  };
  addr('Home address', d.personalHomeAddress);
  addr('Postal address', d.personalPostalAddress);

  const debt = d.personalEducationAndOtherDebt;
  if (debt?.haveHelpDept) rows.push({ label: 'HELP debt', value: debt.remainingHelpDebt ? formatAud(debt.remainingHelpDebt) : 'Yes' });
  if (debt?.haveSfssDebt) rows.push({ label: 'SFSS debt', value: debt.remainingSfssDebt ? formatAud(debt.remainingSfssDebt) : 'Yes' });
  if (debt?.haveOtherTaxRelatedDebts) rows.push({ label: 'Other tax-related debts', value: 'Yes' });

  return rows;
}

// Income-test items (IT1–IT7) that have a value — adjustments used for various thresholds.
function incomeTestLines(d: ReturnDetail): DisplayLine[] {
  const it = d.incomeTest;
  if (!it) return [];
  const items: [string, number | undefined][] = [
    ['IT1 Reportable fringe benefits', it.iT1TotalReportableFringeBenefits],
    ['IT2 Reportable super contributions', it.iT2ReportableSuperContributions],
    ['IT3 Tax-free government pensions', it.iT3TaxFreeGovernmentPensions],
    ['IT4 Target foreign income', it.iT4TargetForeignIncome],
    ['IT5 Net financial investment loss', it.iT5NetFinancialInvestmentLoss],
    ['IT6 Net rental property loss', it.iT6NetRentalPropertyLoss],
    ['IT7 Child support paid', it.iT7ChildSupportPaid],
  ];
  return items.filter(([, v]) => (v ?? 0) > 0).map(([label, v]) => ({ label, amount: v! }));
}

function LineList({ lines, emptyText }: { lines: DisplayLine[]; emptyText: string }) {
  if (lines.length === 0) {
    return <p className="text-[13px] text-(--color-ink-muted)">{emptyText}</p>;
  }
  return (
    <ul className="divide-y divide-(--color-rule)">
      {lines.map((l, idx) => (
        <li key={`${l.label}-${idx}`} className="flex items-center justify-between py-3">
          <div>
            <div className="text-[14px] font-medium text-(--color-ink)">{l.label}</div>
            {l.sub && <div className="text-[12px] text-(--color-ink-muted)">{l.sub}</div>}
          </div>
          <div className="font-mono text-[14px] font-semibold tabular-nums text-(--color-ink)">
            {l.amount === null ? (
              <span className="text-[12px] font-medium text-(--color-ink-muted)">Provided</span>
            ) : (
              formatAud(l.amount)
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function OverviewTab({ detail, loading }: { detail?: ReturnDetail; loading: boolean }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-[14px] text-(--color-ink-muted)">
          Loading return…
        </CardContent>
      </Card>
    );
  }
  if (!detail) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-[14px] text-(--color-ink-muted)">
          No return data to show.
        </CardContent>
      </Card>
    );
  }

  const pi = detail.personalInformation;
  const phone = [pi?.areaCode, pi?.phoneNumber].filter(Boolean).join(' ').trim();

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="space-y-4 p-7">
          <div>
            <div className="section-ref">Personal</div>
            <h2 className="mt-1 font-serif text-xl font-medium">Identity & contact</h2>
          </div>
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {pi ? (
              <>
                <Field label="Name" value={`${pi.firstName ?? ''} ${pi.familyName ?? ''}`.trim() || '—'} />
                <Field label="Date of birth" value={pi.dateOfBirth?.slice(0, 10) || '—'} />
                <Field label="Mobile" value={pi.mobilePhone || '—'} />
                <Field label="Phone" value={phone || '—'} />
                <Field label="Tax file number" value={pi.taxFileNumber || 'Not yet provided'} />
                <Field label="Occupation" value={detail.occupationDetail?.occupation || '—'} />
                <Field label="Final return" value={pi.isFinalReturn ? 'Yes' : 'No'} />
              </>
            ) : (
              <div className="text-[13px] text-(--color-ink-muted) sm:col-span-2">
                No personal data on this return yet.
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {personalDetailLines(detail).length > 0 && (
        <Card>
          <CardContent className="space-y-4 p-7">
            <div>
              <div className="section-ref">Personal</div>
              <h2 className="mt-1 font-serif text-xl font-medium">Residency, bank & debts</h2>
            </div>
            <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
              {personalDetailLines(detail).map((r) => (
                <Field key={r.label} label={r.label} value={r.value} />
              ))}
            </dl>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-4 p-7">
          <div>
            <div className="section-ref">Income</div>
            <h2 className="mt-1 font-serif text-xl font-medium">Reported income</h2>
          </div>
          <LineList lines={incomeLines(detail)} emptyText="None reported." />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-7">
          <div>
            <div className="section-ref">Deductions</div>
            <h2 className="mt-1 font-serif text-xl font-medium">Claimed deductions</h2>
          </div>
          <LineList lines={deductionLines(detail)} emptyText="None reported." />
        </CardContent>
      </Card>

      {(detail.rentalProperties?.length ?? 0) > 0 && (
        <Card>
          <CardContent className="space-y-4 p-7">
            <div>
              <div className="section-ref">Rental</div>
              <h2 className="mt-1 font-serif text-xl font-medium">Rental properties</h2>
            </div>
            <LineList lines={rentalLines(detail)} emptyText="None." />
          </CardContent>
        </Card>
      )}

      {detail.cryptoHolding && (
        <Card>
          <CardContent className="space-y-4 p-7">
            <div>
              <div className="section-ref">Crypto</div>
              <h2 className="mt-1 font-serif text-xl font-medium">Cryptocurrency</h2>
            </div>
            <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
              <Field label="Held or traded crypto" value={detail.cryptoHolding.hasCrypto ? 'Yes' : 'No'} />
              <Field label="Net capital gain / loss" value={formatAud(detail.cryptoHolding.netProfitOrLoss ?? 0)} />
              <Field label="Held over 12 months" value={detail.cryptoHolding.heldOverTwelveMonths ? 'Yes' : 'No'} />
            </dl>
          </CardContent>
        </Card>
      )}

      {detail.soleTraderBusiness?.hasSoleTraderIncome && (() => {
        const b = detail.soleTraderBusiness!;
        const income = (b.grossSalesTurnover ?? 0) + (b.otherBusinessIncome ?? 0);
        const expenses =
          (b.purchases ?? 0) + (b.contractorPayments ?? 0) + (b.employeeWages ?? 0) +
          (b.employeeSuperannuation ?? 0) + (b.rentOrLease ?? 0) + (b.motorVehicleExpenses ?? 0) +
          (b.repairsAndMaintenance ?? 0) + (b.depreciation ?? 0) + (b.insurance ?? 0) +
          (b.interestExpenses ?? 0) + (b.utilities ?? 0) + (b.phoneAndInternet ?? 0) +
          (b.advertising ?? 0) + (b.officeSupplies ?? 0) + (b.accountingFees ?? 0) +
          (b.bankFees ?? 0) + (b.travel ?? 0) + (b.otherExpenses ?? 0);
        return (
          <Card>
            <CardContent className="space-y-4 p-7">
              <div>
                <div className="section-ref">Sole trader</div>
                <h2 className="mt-1 font-serif text-xl font-medium">Sole trader / business</h2>
              </div>
              <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                <Field label="Registered for GST" value={b.isRegisteredForGst ? 'Yes' : 'No'} />
                <Field
                  label="Business name(s)"
                  value={(b.businessNames ?? []).filter(Boolean).join(', ') || '—'}
                />
                <Field label="Total business income" value={formatAud(income)} />
                <Field label="Total business expenses" value={formatAud(expenses)} />
                <Field label="Net business profit" value={formatAud(income - expenses)} />
              </dl>
            </CardContent>
          </Card>
        );
      })()}

      {otherDetailLines(detail).length > 0 && (
        <Card>
          <CardContent className="space-y-4 p-7">
            <div>
              <div className="section-ref">Other</div>
              <h2 className="mt-1 font-serif text-xl font-medium">Other details</h2>
            </div>
            <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
              {otherDetailLines(detail).map((r) => (
                <Field key={r.label} label={r.label} value={r.value} />
              ))}
            </dl>
          </CardContent>
        </Card>
      )}

      {incomeTestLines(detail).length > 0 && (
        <Card>
          <CardContent className="space-y-4 p-7">
            <div>
              <div className="section-ref">Income test</div>
              <h2 className="mt-1 font-serif text-xl font-medium">Income-test adjustments</h2>
            </div>
            <LineList lines={incomeTestLines(detail)} emptyText="None." />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sync — per-destination ledger status (GET) with a manual "Sync now" (POST)
// that marks the return's SyncState rows Pending for the reconcilers to drain.
// ---------------------------------------------------------------------------

interface SyncStateRow {
  destination: string;
  status: string;
  dirtyAt: string;
  lastSyncedAt?: string | null;
  attemptCount: number;
  lastError?: string | null;
}

interface SyncStatusResult {
  taxReturnId: number;
  states: SyncStateRow[];
}

type PillTone = 'success' | 'navy' | 'warning' | 'danger' | 'neutral';

function syncTone(status: string): PillTone {
  switch (status) {
    case 'Synced':
      return 'success';
    case 'Syncing':
      return 'navy';
    case 'Pending':
      return 'warning';
    case 'Failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

function SyncTab({ returnId }: { returnId: string }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const key = ['admin', 'returns', returnId, 'sync'];

  const sync = useQuery({
    queryKey: key,
    queryFn: () => api<SyncStatusResult>(`/returns/${returnId}/sync`),
    retry: false,
  });

  const runSync = useMutation({
    mutationFn: () => api<SyncStatusResult>(`/returns/${returnId}/sync`, { method: 'POST' }),
    onError: (e: unknown) => setError(extractError(e)),
    onSuccess: (data) => {
      setError(null);
      qc.setQueryData(key, data);
    },
  });

  const states = sync.data?.states ?? [];

  return (
    <Card>
      <CardContent className="space-y-4 p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="section-ref">Sync</div>
            <h2 className="mt-1 font-serif text-xl font-medium">External systems</h2>
            <p className="mt-1 text-[13px] text-(--color-ink-muted)">
              Reconciliation status per destination. “Sync now” queues an immediate re-push.
            </p>
          </div>
          <Button onClick={() => runSync.mutate()} disabled={runSync.isPending}>
            <RefreshCw className={cn('size-4', runSync.isPending && 'animate-spin')} />
            {runSync.isPending ? 'Queuing…' : 'Sync now'}
          </Button>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-(--radius) border border-(--color-maroon) bg-(--color-maroon-soft) px-3 py-2 text-[13px] text-(--color-maroon)">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {sync.isLoading ? (
          <p className="py-6 text-center text-[13px] text-(--color-ink-muted)">Loading sync status…</p>
        ) : states.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-(--color-ink-muted)">
            This return has not been queued for sync yet. Use “Sync now” to push it to PracticeManager and Ontraport.
          </p>
        ) : (
          <ul className="divide-y divide-(--color-rule)">
            {states.map((s) => (
              <li key={s.destination} className="flex items-start justify-between gap-4 py-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2.5">
                    <span className="text-[14px] font-medium text-(--color-ink)">{s.destination}</span>
                    <StatusPill tone={syncTone(s.status)}>{s.status}</StatusPill>
                  </div>
                  <div className="text-[12px] text-(--color-ink-muted)">
                    {s.lastSyncedAt
                      ? `Last synced ${new Date(s.lastSyncedAt).toLocaleString('en-AU')}`
                      : 'Never synced'}
                    {s.attemptCount > 0 && ` · ${s.attemptCount} attempt${s.attemptCount === 1 ? '' : 's'}`}
                  </div>
                  {s.lastError && (
                    <div className="flex items-start gap-1.5 text-[12px] text-(--color-maroon)">
                      <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
                      <span className="break-all">{s.lastError}</span>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The agent transcript for this return.
 *
 * This used to be a placeholder saying transcripts weren't persisted yet — but they were, and the
 * Conversations page had been rendering them from this exact endpoint all along. Same component
 * both places, so the two can't drift apart again.
 */
function TranscriptTab({ returnId }: { returnId: string }) {
  const { messages, isLoading, isError, truncated } = useConversationMessages(returnId);

  return (
    <Card>
      <CardContent className="p-5">
        {messages.length > 0 && (
          <p className="mb-4 text-[12px] text-(--color-ink-muted)">
            {messages.length} message{messages.length === 1 ? '' : 's'}, oldest first. Tool calls
            are collapsed.
          </p>
        )}
        <ConversationMessages
          messages={messages}
          isLoading={isLoading}
          isError={isError}
          truncated={truncated}
          emptyMessage="No agent conversation on this return yet."
        />
      </CardContent>
    </Card>
  );
}

function AuditTab({ returnId }: { returnId: string }) {
  const audit = useQuery({
    queryKey: ['admin', 'audit', returnId],
    // The endpoint returns AuditListResponse ({ items, nextCursor, hasMore }), not a
    // bare array — read .items rather than mapping the wrapper object.
    queryFn: () => api<AdminModuleAuditListResponse>(`/admin/audit?returnId=${returnId}`),
    retry: false,
  });

  if (audit.isError) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-[14px] text-(--color-ink-muted)">
            Audit log is wired but no entries yet. Mutations made via{' '}
            <code className="font-mono text-[12px]">/api/v1/*</code> appear here as the
            AuditLogBehavior records them.
          </p>
        </CardContent>
      </Card>
    );
  }

  const entries = audit.data?.items ?? [];

  return (
    <Card>
      <CardContent className="space-y-2 p-5">
        {entries.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-(--color-ink-muted)">No audit entries.</p>
        ) : (
          <ul className="divide-y divide-(--color-rule)">
            {entries.map((a) => (
              <AuditRow key={a.id} entry={a} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Mirrors the row on the Audit page: the same humanised action, the raw command name kept as
 * subtext for anyone matching it against code, and the payload behind a click.
 *
 * This used to render `entry.action` straight out of the DTO — the MediatR class name, e.g.
 * "SaveWorkRelatedParkingTollExpenseCommand" — and drop the payload entirely, so the same audit
 * entry read very differently depending on which page you opened it from.
 */
function AuditRow({ entry }: { entry: AdminModuleAuditEntry }) {
  const [open, setOpen] = useState(false);
  const hasPayload = !!entry.payload;

  return (
    <li>
      <button
        type="button"
        onClick={() => hasPayload && setOpen((v) => !v)}
        // Non-interactive when there's nothing to reveal, rather than a button that does nothing.
        aria-expanded={hasPayload ? open : undefined}
        disabled={!hasPayload}
        className={cn(
          'flex w-full items-center justify-between gap-3 py-3 text-left text-[13px]',
          hasPayload && 'cursor-pointer hover:bg-(--color-paper-2)',
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <StatusPill tone={entry.source === 'Agent' ? 'navy' : 'neutral'}>
            {entry.source}
          </StatusPill>
          <div className="min-w-0">
            <div className="truncate font-medium text-(--color-ink)">
              {humanizeAuditAction(entry.action)}
            </div>
            <div className="truncate text-[12px] text-(--color-ink-muted)">
              <span className="font-mono text-[11px] text-(--color-ink-subtle)">
                {entry.action ?? ''}
              </span>
              {entry.entity ? ` · ${entry.entity}` : ''}
              {entry.userName ? ` · ${entry.userName}` : ''}
            </div>
          </div>
        </div>
        <div className="shrink-0 font-mono text-[11px] tabular-nums text-(--color-ink-muted)">
          {new Date(entry.timestamp).toLocaleString('en-AU')}
        </div>
      </button>
      {open && hasPayload && (
        <div className="border-t border-(--color-rule) bg-(--color-paper-2) px-4 py-3">
          <div className="section-ref mb-2">Payload (PII-scrubbed)</div>
          <pre className="overflow-x-auto font-mono text-[11px] leading-relaxed text-(--color-ink-2)">
            {prettyAuditJson(entry.payload!)}
          </pre>
        </div>
      )}
    </li>
  );
}

function prettyAuditJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="section-ref">{label}</div>
      <div className="mt-1 text-[14px] font-medium text-(--color-ink)">
        {value || <span className="text-(--color-ink-muted)">—</span>}
      </div>
    </div>
  );
}
